import { useEffect, useState } from "react";
import axios from "axios";
import { RippPanel } from "./primitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REG = { TRENDING_UP: "var(--r2-emerald)", TRENDING_DOWN: "var(--r2-red)", HIGH_VOL: "var(--r2-amber)", EVENT: "var(--r2-amber)", RANGE: "var(--r2-text2)", LOW_VOL: "var(--r2-cyan)", UNCERTAIN: "var(--r2-text2)" };
const num = (v, d = 2) => (v == null ? "—" : Number(v).toFixed(d));
const sgn = (v, d = 2) => (v == null ? "—" : `${v > 0 ? "+" : ""}${Number(v).toFixed(d)}%`);

const Inst = ({ sym, i }) => {
  const r = i?.regime || {};
  return (
    <div className="rounded border border-[var(--r2-border)] p-2 text-[9px] font-mono" data-testid={`fut-intel-${sym}`}>
      <div className="flex items-center justify-between">
        <span className="text-[var(--r2-text1)] tracking-[0.14em]">{sym}</span>
        <span className="uppercase tracking-[0.12em]" style={{ color: REG[r.label] || "var(--r2-text2)" }} data-testid={`fut-intel-${sym}-regime`}>{r.label || "—"}{r.confidence != null ? ` ${Math.round(r.confidence * 100)}%` : ""}</span>
      </div>
      <div className="mt-1 grid grid-cols-3 gap-1 text-[var(--r2-text2)]">
        <span>last <b className="text-[var(--r2-text1)]">{num(i?.last, 0)}</b></span>
        <span>24h <b className="text-[var(--r2-text1)]">{sgn(i?.chg_24h)}</b></span>
        <span>ATR <b className="text-[var(--r2-text1)]">{num(r.atr_pct)}%</b></span>
        <span>{i?.vol?.name} <b className="text-[var(--r2-text1)]">{num(i?.vol?.last, 1)}</b></span>
        <span>vol rank <b className="text-[var(--r2-text1)]">{i?.vol?.pct_rank_30d ?? "—"}</b></span>
        <span>flips/24 <b className="text-[var(--r2-text1)]">{i?.stability?.flips ?? "—"}</b></span>
      </div>
      {i?.event && <div className="mt-1 text-[var(--r2-amber)]" data-testid={`fut-intel-${sym}-event`}>EVENT WINDOW · {i.event.event} ({i.event.in_min > 0 ? `in ${i.event.in_min}m` : `${-i.event.in_min}m ago`})</div>}
      {i?.stale_sec > 0 && <div className="mt-1 text-[var(--r2-amber)]">stale {i.stale_sec}s</div>}
    </div>
  );
};

export const FuturesIntel = () => {
  const [s, setS] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let live = true;
    const load = () => axios.get(`${API}/futures/intel/snapshot`).then((r) => live && setS(r.data)).catch((e) => live && setErr(e.message));
    load(); const t = setInterval(load, 60000);
    return () => { live = false; clearInterval(t); };
  }, []);
  const cal = (s?.calendar || []).filter((e) => e.tier === 1).slice(0, 5);
  return (
    <RippPanel title="Futures intel · OpenBB (informs only)" testid="fut-intel-panel">
      {!s && !err && <div className="text-[9px] font-mono text-[var(--r2-text2)]" data-testid="fut-intel-loading">loading…</div>}
      {err && <div className="text-[9px] font-mono text-[var(--r2-red)]" data-testid="fut-intel-error">{err}</div>}
      {s && !s.flags?.openbb_enabled && <div className="text-[9px] font-mono text-[var(--r2-amber)]" data-testid="fut-intel-disabled">OPENBB_ENABLED=false — research layer off</div>}
      {s && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">{Object.entries(s.instruments || {}).map(([k, v]) => <Inst key={k} sym={k} i={v} />)}</div>
          <div className="grid grid-cols-3 gap-1 text-[9px] font-mono text-[var(--r2-text2)]" data-testid="fut-intel-macro">
            <span>2s10s <b className="text-[var(--r2-text1)]">{s.macro?.yields ? `${(s.macro.yields.curve_2s10s * 100).toFixed(0)}bp` : "—"}</b></span>
            <span>10y <b className="text-[var(--r2-text1)]">{s.macro?.tnx ? `${num(s.macro.tnx.last)} (${sgn(s.macro.tnx.chg_5d, 1)})` : "—"}</b></span>
            <span>DXY <b className="text-[var(--r2-text1)]">{s.macro?.dxy ? `${num(s.macro.dxy.last, 1)} (${sgn(s.macro.dxy.chg_5d, 1)})` : "—"}</b></span>
          </div>
          <div className="text-[9px] font-mono" data-testid="fut-intel-calendar">
            <div className="text-[var(--r2-text2)] uppercase tracking-[0.12em]">tier-1 US releases · next 48h</div>
            {cal.length === 0 && <div className="text-[var(--r2-text2)]">none</div>}
            {cal.map((e, i) => (
              <div key={i} className="flex justify-between" data-testid={`fut-intel-cal-${i}`}>
                <span className="text-[var(--r2-text1)] truncate">{e.event}</span>
                <span className="text-[var(--r2-text2)]">{e.in_min > 0 ? `in ${Math.round(e.in_min / 60)}h${e.in_min % 60}m` : `${-e.in_min}m ago`}</span>
              </div>
            ))}
          </div>
          <div className="text-[8px] font-mono text-[var(--r2-text2)]">openbb calls {s.openbb?.calls} · errors {s.openbb?.errors} · snapshot age {Math.round(Date.now() / 1000 - s.at)}s</div>
        </div>
      )}
    </RippPanel>
  );
};
